import Dataset from '../model/dataset'

import _ from 'lodash'
import $ from 'jquery'
import conductor from 'scoreboard/components/conductor'
import PrincipalPanel from 'scoreboard/components/principal-panel'
import i18n from 'scoreboard/components/i18n'
import icon from 'scoreboard/components/icon'
import guard from 'scoreboard/components/unsaved/guard'
import user from 'scoreboard/components/user'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import PermissionsRollupSelect from './permissions-rollup-select'
import principals from 'scoreboard/components/principals'
import highlight from 'scoreboard/components/highlight'
import Tooltip from 'scoreboard/components/tooltip'

const urlParams = $.param({
	includeRollupTreeGroups: true,
	includeActiveViewers: true,
	includeViewOnly: true
})

/**
 * Base class for all principal panels displayed when editing dataset
 * permissions
 *
 * @property {Dataset} model
 * @extends PrincipalPanel
 */
var DatasetsPrincipalPanel = PrincipalPanel.extend(/** @lends DatasetsPrincipalPanel.prototype */{

	/**
	 *
	 * @property {Dataset} model
	 *
	 * @param {Boolean|Function} options.includeEveryone
	 * @param {Function} options.principalDataRowFn
	 * @param {Boolean} [options.renderTemplateRollupSelect=true] If a rollup select should be
	 * rendered for rollup template principals (false for indirect principals)
	 */
	initialize: function(options = {}) {
		_.defaults(options, {
			includeEveryone: true,
			url: `api/datasets/${this.model.id}/potential-assignees?${urlParams}`,
			renderTemplateRollupSelect: true,
			groupBy: 'group',
			groupSortOrder: ['rollup-tree-group', 'user', 'group'],
			optionDecoratorFn: this.optionDecoratorFn.bind(this)
		})
		PrincipalPanel.prototype.initialize.apply(this, arguments)
		this.viewState = options.viewState
		this.rollupSelects = []

		this.rollupTreeGroupLabels = {
			'group' : i18n.get('common.group.groups'),
			'user' : i18n.get('common.user.users'),
			'rollup-tree-group' : i18n.get('datasets.rollupTreeGroups')
		}
	},

	_principalsAsSet: function(principals) {
		return _.uniqBy(principals, p => [p.id, p.type, p.fieldId].join('_'))
	},

	/**
	 * Make sure we're always pointing to the main row created by permissions-panel.js
	 */
	_getModelRow: function(principalRow) {
		return _.invoke(this.options, 'principalDataRowFn', principalRow) ||
			this.getPrincipalDataRow(principalRow)
	},

	render: function() {
		this.principalsRendered = 0
		PrincipalPanel.prototype.render.apply(this, arguments)
		if (this.viewState && this.viewState.collapsed == false) {
			this.toggle()
		}

		this.addLinkToPrincipalLabels()
		this.on('change:add', () => this.addLinkToPrincipalLabels())
		return this
	},

	optionDecoratorFn: function(option) {
		const {id, type} = option
		option.group = id < 0 ? 'headerless' : this.rollupTreeGroupLabels[type]
	},

	/**
	 * It is the responsibility of the caller to also invoke afterPrincipalAttached()
	 * when the principal row is added to the DOM
	 */
	renderPrincipal: function (principal) {
		var html = PrincipalPanel.prototype.renderPrincipal.apply(this, arguments)
		const principalRow = $(html)

		if (principals.equals(user.principal, principal)) {
			principalRow.find('.delete').addClass('is-not-displayed')
			return principalRow // Don't allow user to modify or remove their own permissions
		}
		// Mark principals that can no longer view the org
		if (principal.inactive) {
			let label = principalRow.find('.principal-label')
				.after(`<span class="permissions-removed">${icon('warn')}</span>`)
			this.permissionsRemovedTooltip = new TooltipAndDetails({
				el: this.el,
				selector: label.next('.permissions-removed'),
				tooltipContent: () => {
					return i18n.get('datasets.lostPermissions',
						_.lowerCase(i18n.get(`objectHistoryType.${principal.type}`)),
						highlight(this.model.get('organizationLabel')))
				},
				tooltipOptions: {
					extraClass: 'has-error',
					placement: 'above'
				}
			})
		}
		// add a settings button
		principalRow.find('.table-actions-cell ul').prepend(
			'<li>' +
			'<a class="settings button button-covert" data-action="' + i18n.get(
			'common.edit') + '">' +
			icon('edit') +
			'</a>' +
			'</li>'
		)
		return principalRow
	},

	afterPrincipalAttached: function(principalRow, principal) {
		// add additional data attributes (needed to load the model row)
		principalRow.attr('data-field-id', principal.fieldId)
		principalRow.attr('data-tree-id', principal.treeRootId)

		const modelRow = this._getModelRow(principalRow)
		// associate each row with its permissions
		if (this.options.currentPermissions) {
			modelRow.data('permissions', this.options.currentPermissions[this.principalsRendered++])
		}

		// add rollup selects
		if (this.options.renderTemplateRollupSelect) {
			this._renderRollupSelect(principal, principalRow, modelRow)
		}

		// 'Everyone' group label shouldn't be a text-link and should feature an explanatory tooltip.
		// Both the administrator user and the everyone group have an id of -1 so also check principal type.
		if (principal.id == -1 && principal.type != 'user') {
			principalRow.find('.principal-label').removeClass('text-link')
			let label = principalRow.find('.principal-label')
			label.addClass('no-link')
				.on('mouseenter', () => {
					this.everyoneTooltip = new Tooltip({
						attachTo: label,
						content: i18n.get('datasets.permissions.everyone.tooltip'),
						placement: 'above',
						extraClass: 'button-covert-tooltip'
					}).render()
				})
				.on('mouseleave', () => {
					_.invoke(this.everyoneTooltip, 'remove')
				})
		}

		if (principal.id == -1) {
			principalRow.find('.principal-label').removeClass('text-link')
		}
	},

	_renderRollupSelect: function (principal, principalRow, modelRow) {
		if (principal.rollupPermission || principal.type === Dataset.ROLLUP_TREE_GROUP) {
			// Direct permission rollup template principals return the
			// rollupPermission for the selected tree node; so we will default
			// to that since it must be used instead of the built-in rollup
			// filter
			let selectedValue = principal.rollupPermission
			if (_.isNil(selectedValue)) {
				selectedValue = this.model.getBuiltInValueForTemplatePrincipal(
					principal)
			}
			const rollupSelect = new PermissionsRollupSelect({
				// Place the rollup select in the same cell as the name so that
				// we can have more flexibility when it comes to altering its
				// size to be as large as needed according to what's around it.
				el: principalRow.find('.principal-name'),
				principal,
				modelRow,
				selectedValue
			}).render()

			// Keep track of all the rollup selects for this principal so we can
			// update the option when the user changes it any of 4 places
			const modelRollupSelects = modelRow.data('rollupSelects') || []
			modelRollupSelects.push(rollupSelect)
			modelRow.data('rollupSelects', modelRollupSelects)

			this.rollupSelects.push(rollupSelect)
			rollupSelect.on('change', opts => {
				this.onChangeRollupSelect(opts)
				this.trigger('change:rollup-permission', opts)
			})
			principalRow.addClass('has-dataset-rollup-select')
		}
	},

	/**
	 * Update all the selects for a single permission with the newly chosen rollup option
	 */
	syncRollupOption: function (modelRow, rollupValue) {
		_.invokeMap(modelRow.data('rollupSelects'), 'val', rollupValue)
	},

	/**
	 * Subclasses can implement this to handle changes in a rollup value
	 * select. Alternatively, other components can listen for
	 * 'change:rollup-permission'
	 *
	 * @param {Element|JQuery} modelRow
	 * @param {Principal} principal The principal for the row containing the select
	 * @param {String} rollupPermission The currently selected value
	 */
	onChangeRollupSelect: function({modelRow, principal, rollupPermission}) {
		const {permissions} = modelRow.data()
		const {treeRootId} = principal
		const filter = _.find(permissions.filters, Dataset.isBuiltInFilter)
		if (filter && treeRootId) {
			// There are two ways a rollup select can be shown
			// (1) A principal that is a rollup template
			// (2) A user/group that has permission to a rollup tree (#35058)
			// Here we are only worried about responding to changes to (1),
			// because (2) only shows the select in one panel (direct permission)
			// whereas (1) has a select in two panels (and the dataset-permissions-form)
			filter.rollupIsAnyFilters[treeRootId] = rollupPermission
			this.syncRollupOption(modelRow, rollupPermission)
		}
	},

	readPrincipalOptionsFromDomRow: function (row, principal) {
		principal.fieldId = row.data('fieldId')
	},

	addLinkToPrincipalLabels: function () {
		// #31445 add text-link appearance to principal name
		this.$('tr:not([data-id=-1]) .principal-label:not(.no-link)')
			.addClass('text-link')
			.on('click', e => this.navigateToPrincipal(e))
	},

	navigateToPrincipal: function (e) {
		let principalItem = this.$(e.target).closest('[data-id]')
		const {id, type, treeRootId} = this._getModelRow(principalItem).data().permissions.principal
		let url
		if (type === 'rollup-tree-group') {
			url = `admin/dataset-trees/${treeRootId}/rollup-tree-groups/${id}`
		} else {
			url = `admin/${type}s`
			if (id !== -1) {
				url += `/${id}`
			}
		}
		guard.conditionallyRun(() => {
			conductor.navigate(url, {trigger: true})
		})
	},

	remove: function() {
		PrincipalPanel.prototype.remove.apply(this, arguments)
		_.invokeMap([...this.rollupSelects, this.permissionsRemovedTooltip, this.everyoneTooltip], 'remove')
	}
})

export default DatasetsPrincipalPanel

DatasetsPrincipalPanel.__filename__ = 'datasets/edit/datasets-principal-panel.js'
